import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FaKey, FaPlus, FaTrash, FaCheck, FaTimes, FaChartLine, FaUsers, FaRobot } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const NovayarAdminPanel = ({ token }) => {
  const [activeSubTab, setActiveSubTab] = useState('stats'); // stats, api-keys, subscriptions, users
  const [stats, setStats] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [users, setUsers] = useState({ guests: [], registered_users: [] });
  const [loading, setLoading] = useState(false);
  const [showAddKeyModal, setShowAddKeyModal] = useState(false);
  const [newKeyData, setNewKeyData] = useState({ key: '', name: '' });

  useEffect(() => {
    loadStats();
    loadApiKeys();
    loadSubscriptions();
    loadUsers();
  }, [token]);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/novayar-admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadApiKeys = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/novayar-admin/api-keys`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setApiKeys(response.data);
    } catch (error) {
      console.error('Error loading API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/novayar-admin/subscription-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSubscriptions(response.data);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/novayar-admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddKey = async () => {
    if (!newKeyData.key) {
      alert('لطفاً کلید API را وارد کنید');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/novayar-admin/api-keys`,
        newKeyData,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setShowAddKeyModal(false);
      setNewKeyData({ key: '', name: '' });
      loadApiKeys();
      alert('کلید با موفقیت اضافه شد');
    } catch (error) {
      alert(error.response?.data?.detail || 'خطا در افزودن کلید');
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!window.confirm('آیا از حذف این کلید اطمینان دارید?')) return;

    try {
      await axios.delete(`${API_URL}/api/novayar-admin/api-keys/${keyId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadApiKeys();
      alert('کلید با موفقیت حذف شد');
    } catch (error) {
      alert('خطا در حذف کلید');
    }
  };

  const handleApproveSubscription = async (requestId) => {
    try {
      await axios.put(
        `${API_URL}/api/novayar-admin/subscription-requests/${requestId}/approve`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      loadSubscriptions();
      loadStats();
      alert('اشتراک با موفقیت تایید شد');
    } catch (error) {
      alert('خطا در تایید اشتراک');
    }
  };

  const handleRejectSubscription = async (requestId) => {
    try {
      await axios.put(
        `${API_URL}/api/novayar-admin/subscription-requests/${requestId}/reject`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      loadSubscriptions();
      alert('درخواست رد شد');
    } catch (error) {
      alert('خطا در رد درخواست');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fa-IR');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-2">
          <FaRobot className="text-4xl" />
          <div>
            <h2 className="text-3xl font-bold">پنل مدیریت نوآیار</h2>
            <p className="text-blue-100">دستیار هوشمند سامانه نوآفرین شو</p>
          </div>
        </div>
      </div>

      {/* Sub Tabs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 bg-white dark:bg-gray-800 p-2 rounded-lg shadow">
        <button
          onClick={() => setActiveSubTab('stats')}
          className={`py-3 px-4 rounded-lg font-bold transition-all ${
            activeSubTab === 'stats'
              ? 'bg-blue-600 text-white shadow-lg'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          <FaChartLine className="inline mr-2" />
          آمار
        </button>
        <button
          onClick={() => setActiveSubTab('users')}
          className={`py-3 px-4 rounded-lg font-bold transition-all ${
            activeSubTab === 'users'
              ? 'bg-blue-600 text-white shadow-lg'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          <FaUsers className="inline mr-2" />
          کاربران
        </button>
        <button
          onClick={() => setActiveSubTab('api-keys')}
          className={`py-3 px-4 rounded-lg font-bold transition-all ${
            activeSubTab === 'api-keys'
              ? 'bg-blue-600 text-white shadow-lg'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          <FaKey className="inline mr-2" />
          API Keys
        </button>
        <button
          onClick={() => setActiveSubTab('subscriptions')}
          className={`py-3 px-4 rounded-lg font-bold transition-all ${
            activeSubTab === 'subscriptions'
              ? 'bg-blue-600 text-white shadow-lg'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          <FaUsers className="inline mr-2" />
          <span className="hidden lg:inline">درخواست‌های اشتراک</span>
          <span className="lg:hidden">درخواست‌ها</span>
          {stats && stats.pending_subscriptions > 0 && (
            <span className="mr-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
              {stats.pending_subscriptions}
            </span>
          )}
        </button>
      </div>

      {/* Stats Tab */}
      {activeSubTab === 'stats' && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={<FaRobot className="text-4xl text-blue-600" />}
            title="کل چت‌ها"
            value={stats.total_chats}
            bgColor="bg-blue-50 dark:bg-blue-900/20"
          />
          <StatCard
            icon={<FaUsers className="text-4xl text-green-600" />}
            title="کاربران مهمان"
            value={stats.total_guests}
            bgColor="bg-green-50 dark:bg-green-900/20"
          />
          <StatCard
            icon={<FaCheck className="text-4xl text-purple-600" />}
            title="اشتراک‌های فعال"
            value={stats.active_subscriptions}
            bgColor="bg-purple-50 dark:bg-purple-900/20"
          />
          <StatCard
            icon={<FaKey className="text-4xl text-orange-600" />}
            title="API Keys فعال"
            value={`${stats.active_api_keys} / ${stats.total_api_keys}`}
            bgColor="bg-orange-50 dark:bg-orange-900/20"
          />
        </div>
      )}

      {/* API Keys Tab */}
      {activeSubTab === 'api-keys' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-gray-800 dark:text-white">مدیریت API Keys</h3>
            <button
              onClick={() => setShowAddKeyModal(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <FaPlus /> افزودن کلید جدید
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">نام</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">کلید</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">وضعیت</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">آخرین استفاده</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {apiKeys.map((key) => (
                  <tr key={key.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {key.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600 dark:text-gray-400">
                      {key.key.substring(0, 20)}...
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        key.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {key.status === 'active' ? 'فعال' : 'غیرفعال'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {key.last_used ? formatDate(key.last_used) : 'هرگز'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleDeleteKey(key.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeSubTab === 'users' && (
        <div className="space-y-6">
          {/* کاربران مهمان */}
          <div>
            <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
              کاربران مهمان ({users.total_guests || 0})
            </h3>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">نام</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">موبایل</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">تعداد چت</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">استفاده امروز</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">وضعیت اشتراک</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">تاریخ ثبت</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {users.guests && users.guests.map((guest) => (
                    <tr key={guest.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {guest.first_name} {guest.last_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {guest.mobile}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {guest.chat_count || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {guest.usage_count || 0} / 5
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          guest.has_active_subscription
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {guest.has_active_subscription ? 'فعال ✨' : 'ندارد'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {guest.created_at ? formatDate(guest.created_at) : '-'}
                      </td>
                    </tr>
                  ))}
                  {(!users.guests || users.guests.length === 0) && (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                        هیچ کاربر مهمانی یافت نشد
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* کاربران ثبت‌نام شده */}
          <div>
            <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
              کاربران ثبت‌نام شده ({users.total_registered || 0})
            </h3>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">نام</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">موبایل</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">هسته فناور</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">تعداد چت</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">استفاده امروز</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">وضعیت اشتراک</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {users.registered_users && users.registered_users.map((user) => (
                    <tr key={user.mobile}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {user.first_name} {user.last_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {user.mobile}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {user.tech_core_name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {user.chat_count || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {user.usage_count || 0} / 5
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          user.has_active_subscription
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.has_active_subscription ? 'فعال ✨' : 'ندارد'}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {(!users.registered_users || users.registered_users.length === 0) && (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                        هیچ کاربر ثبت‌نام شده‌ای یافت نشد
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Subscriptions Tab */}
      {activeSubTab === 'subscriptions' && (
        <div>
          <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">درخواست‌های اشتراک</h3>

          <div className="space-y-4">
            {subscriptions.map((sub) => (
              <div key={sub.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-bold text-lg text-gray-800 dark:text-white mb-2">
                      {sub.user_info?.first_name} {sub.user_info?.last_name}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      📱 {sub.user_info?.mobile}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      🔖 نوع: {sub.subscription_type === 'weekly' ? 'یک هفته‌ای' : 'یک ماهه'}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      📅 تاریخ درخواست: {formatDate(sub.requested_at)}
                    </p>
                    <p className="text-sm mt-2">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        sub.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : sub.status === 'approved'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {sub.status === 'pending' ? 'در انتظار بررسی' : sub.status === 'approved' ? 'تایید شده' : 'رد شده'}
                      </span>
                    </p>
                  </div>

                  <div>
                    {sub.receipt_file && (
                      <div className="mb-3">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">رسید پرداخت:</p>
                        <img
                          src={sub.receipt_file}
                          alt="Receipt"
                          className="w-full h-48 object-contain border rounded-lg"
                        />
                      </div>
                    )}

                    {sub.status === 'pending' && (
                      <div className="flex gap-2 mt-4">
                        <button
                          onClick={() => handleApproveSubscription(sub.id)}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
                        >
                          <FaCheck /> تایید
                        </button>
                        <button
                          onClick={() => handleRejectSubscription(sub.id)}
                          className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
                        >
                          <FaTimes /> رد
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {subscriptions.length === 0 && (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                هیچ درخواستی موجود نیست
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Key Modal */}
      {showAddKeyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">افزودن API Key جدید</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  نام (اختیاری)
                </label>
                <input
                  type="text"
                  value={newKeyData.name}
                  onChange={(e) => setNewKeyData({ ...newKeyData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="مثال: Key 1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  API Key <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newKeyData.key}
                  onChange={(e) => setNewKeyData({ ...newKeyData, key: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                  rows="3"
                  placeholder="کلید API را وارد کنید"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleAddKey}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                >
                  افزودن
                </button>
                <button
                  onClick={() => setShowAddKeyModal(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
                >
                  انصراف
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon, title, value, bgColor }) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    className={`${bgColor} rounded-lg p-6 shadow-lg`}
  >
    <div className="flex items-center justify-between mb-3">
      {icon}
    </div>
    <h4 className="text-gray-700 dark:text-gray-300 text-sm font-medium mb-1">{title}</h4>
    <p className="text-3xl font-bold text-gray-900 dark:text-white">{value}</p>
  </motion.div>
);

export default NovayarAdminPanel;
